import React, { useState, useRef } from 'react';
import { PlusCircle } from 'lucide-react';
import { CandidateInput } from './CandidateInput';
import { PollTimingInput } from './PollTimingInput';
import { useRecaptcha } from '../../hooks/useRecaptcha';
import { useToast } from '../../hooks/useToast';
import { hasDuplicateCandidates } from '../../utils/pollUtils';
import type { PollCreatorProps, PollTiming } from './types';

export function PollCreator({ onCreatePoll }: PollCreatorProps) {
  const [pollName, setPollName] = useState('');
  const [candidates, setCandidates] = useState<string[]>(['', '']);
  const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);
  const [allowNominations, setAllowNominations] = useState(false);
  const [showSharingIcons, setShowSharingIcons] = useState(false);
  const [singleUpvoteOnly, setSingleUpvoteOnly] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>();
  const { verifyHuman } = useRecaptcha();
  const { showToast } = useToast();
  const candidateRefs = useRef<(HTMLInputElement | null)[]>([]);

  const hasDuplicates = hasDuplicateCandidates(candidates);
  const validCandidates = candidates.filter(c => c.trim() !== '');
  const canSubmit = !hasDuplicates && 
    pollName.trim() !== '' && 
    (allowNominations || validCandidates.length >= 2);

  const getDisabledReason = () => {
    if (!pollName.trim()) return "Please enter a poll name";
    if (hasDuplicates) return "Please remove duplicate candidates";
    if (!allowNominations && validCandidates.length < 2) return "Please add at least 2 candidates";
    return "";
  };

  const addCandidate = () => {
    const newIndex = candidates.length;
    setCandidates([...candidates, '']);
    setLastAddedIndex(newIndex);
  };

  const handleCandidateEnter = (index: number) => {
    if (index === candidates.length - 1) {
      addCandidate();
    } else {
      // Focus next candidate input
      candidateRefs.current[index + 1]?.focus();
    }
  };

  const handlePollNameEnter = () => {
    // Focus first candidate input
    candidateRefs.current[0]?.focus();
  };

  const removeCandidate = (index: number) => {
    if (candidates.length <= 2) return;
    const newCandidates = [...candidates];
    newCandidates.splice(index, 1);
    setCandidates(newCandidates);
    candidateRefs.current = candidateRefs.current.filter((_, i) => i !== index);
  };

  const updateCandidate = (index: number, value: string) => {
    const newCandidates = [...candidates];
    newCandidates[index] = value;
    setCandidates(newCandidates);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canSubmit) {
      showToast(getDisabledReason());
      return;
    }

    const isHuman = await verifyHuman();
    if (!isHuman) {
      showToast('Please verify that you are human');
      return;
    }

    const settings = {
      allowNominations,
      showSharingIcons,
      singleUpvoteOnly,
      expiresAt
    };
    onCreatePoll(pollName.trim(), validCandidates, settings);
  };

  return (
    <div className="w-full max-w-md mx-auto p-6">
      <h1 className="text-2xl font-bold text-center mb-6 dark:text-white">Create a Poll</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="pollName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Poll Name
          </label>
          <input
            type="text"
            id="pollName"
            value={pollName}
            onChange={(e) => setPollName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePollNameEnter()}
            placeholder="Enter poll name"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
            required
          />
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Candidates
          </label>
          {candidates.map((candidate, index) => (
            <CandidateInput
              key={index}
              ref={el => candidateRefs.current[index] = el}
              value={candidate}
              index={index}
              showRemove={candidates.length > 2}
              onChange={updateCandidate}
              onRemove={removeCandidate}
              onEnter={handleCandidateEnter}
              autoFocus={index === lastAddedIndex}
            />
          ))}
          
          <button
            type="button"
            onClick={addCandidate}
            className="flex items-center gap-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <PlusCircle size={20} />
            Add Another Candidate
          </button>
        </div>
        
        <PollTimingInput
          expiresAt={expiresAt}
          onChange={setExpiresAt}
        />

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allowNominations"
              checked={allowNominations}
              onChange={(e) => setAllowNominations(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="allowNominations" className="text-sm text-gray-700 dark:text-gray-300">
              Allow voters to nominate additional candidates
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showSharingIcons"
              checked={showSharingIcons}
              onChange={(e) => setShowSharingIcons(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="showSharingIcons" className="text-sm text-gray-700 dark:text-gray-300">
              Show poll sharing icons to voters
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="singleUpvoteOnly"
              checked={singleUpvoteOnly}
              onChange={(e) => setSingleUpvoteOnly(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <label htmlFor="singleUpvoteOnly" className="text-sm text-gray-700 dark:text-gray-300">
              Each voter allowed one upvote
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          title={!canSubmit ? getDisabledReason() : "Create poll"}
          className={`w-full py-3 rounded-lg text-white transition-colors ${
            canSubmit 
              ? 'bg-blue-500 hover:bg-blue-600' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          Create Poll
        </button>
      </form>
    </div>
  );
}